import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { zafyaChat, submitZafyaFeedback } from '../utils/api';

// Zafya's replies are LLM-generated markdown (bold, numbered/bulleted lists,
// paragraphs) — remarkBreaks treats single newlines as line breaks too,
// since chat-style AI output often isn't strictly blank-line-separated
// CommonMark. Component overrides keep spacing/typography consistent with
// the dark purple theme instead of react-markdown's unstyled defaults.
const markdownComponents = {
  p: ({ children }) => <p className="mb-3 last:mb-0 leading-relaxed">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => <ul className="mb-3 last:mb-0 pl-5 space-y-1 list-disc marker:text-purple-400">{children}</ul>,
  ol: ({ children }) => <ol className="mb-3 last:mb-0 pl-5 space-y-1 list-decimal marker:text-purple-400">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  h1: ({ children }) => <h4 className="font-semibold text-white mt-3 mb-1.5 first:mt-0">{children}</h4>,
  h2: ({ children }) => <h4 className="font-semibold text-white mt-3 mb-1.5 first:mt-0">{children}</h4>,
  h3: ({ children }) => <h4 className="font-semibold text-white mt-3 mb-1.5 first:mt-0">{children}</h4>,
  a: ({ children, href }) => (
    <a href={href} target="_blank" rel="noreferrer" className="text-purple-400 hover:text-purple-300 underline break-words">
      {children}
    </a>
  ),
  code: ({ children }) => <code className="bg-black/40 rounded px-1 py-0.5 text-xs break-words">{children}</code>,
};

const ZafyaMarkdown = ({ children }) => (
  <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownComponents}>
    {children}
  </ReactMarkdown>
);

/**
 * Zafya AI panel — shows the same finding in two registers: a clinician-facing
 * summary and a plain patient-facing summary. Also supports live follow-up
 * questions via Zafya AI (Gemini-powered), tailored to whichever register
 * ("For Clinicians" / "For Patients") is currently selected, and grounded in
 * the current screening result (modality, finding, severity, risk, age/sex).
 *
 * When analysisId is set, each turn is persisted server-side against that
 * analysis (see /api/zafya/chat's analysis_id) and the assistant's replies
 * get inline thumbs up/down feedback — the training-signal capture point for
 * the superadmin dashboard.
 */
// Patient-facing render languages. English is the default and the ONLY
// clinician-validated option — the others are generated in English, safety
// validated, then translated, and are labelled as unvalidated in the UI until
// native-speaker clinical review is complete. One language at a time: a reply
// is rendered in the selected language only, never all at once.
const LANGUAGES = [
  { code: 'en',  label: 'English',  validated: true },
  { code: 'pcm', label: 'Pidgin',   validated: false },
  { code: 'ig',  label: 'Igbo',     validated: false },
  { code: 'yo',  label: 'Yoruba',   validated: false },
  { code: 'ha',  label: 'Hausa',    validated: false },
];

const AssistantPanel = ({ assistant, disclaimer, severity, risk, assistantName, chatContext, analysisId }) => {
  const [view, setView] = useState('clinician');
  const [language, setLanguage] = useState('en');
  const [messages, setMessages] = useState([]); // [{role:'user'|'assistant', content}]
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [chatError, setChatError] = useState(null);
  const scrollRef = useRef(null);

  const name = assistantName || 'Zafya AI';

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  if (!assistant) return null;

  const riskColor =
    risk === 'malignant'
      ? 'text-red-400'
      : risk === 'pre-malignant'
      ? 'text-yellow-400'
      : 'text-green-400';

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setChatError(null);
    const nextMessages = [...messages, { role: 'user', content: text }];
    setMessages(nextMessages);
    setInput('');
    setSending(true);
    try {
      const token = localStorage.getItem('guest_token');
      const resp = await zafyaChat(token, {
        message: text,
        history: nextMessages,
        audience: view === 'clinician' ? 'doctor' : 'patient',
        context: chatContext || null,
        analysisId,
        language,
      });
      setMessages((cur) => [
        ...cur,
        {
          role: 'assistant',
          content: resp.reply,
          turnId: resp.turn_id,
          // Carried per-message so the badge reflects the language that reply
          // was actually served in — not whatever is selected now.
          language: resp.language || 'en',
          languageRequested: resp.language_requested || 'en',
          languageDowngraded: !!resp.language_downgraded,
          languageValidated: resp.language_validated !== false,
          offline: !!resp.offline,
        },
      ]);
    } catch (err) {
      setChatError(err.message || 'Zafya AI could not respond — please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleFeedback = async (turnId, rating) => {
    if (!analysisId || !turnId) return;
    setMessages((cur) => cur.map((m) => (m.turnId === turnId ? { ...m, feedbackSent: rating } : m)));
    try {
      const token = localStorage.getItem('guest_token');
      await submitZafyaFeedback(token, analysisId, { turnId, rating });
    } catch (err) {
      // Non-critical — don't block the conversation on a failed feedback POST.
      setMessages((cur) => cur.map((m) => (m.turnId === turnId ? { ...m, feedbackSent: null } : m)));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="bg-gray-900 rounded-2xl shadow-md overflow-hidden border border-gray-800">
      <div className="p-4 sm:p-5 border-b border-gray-800 flex flex-col sm:flex-row gap-3 sm:gap-0 sm:justify-between sm:items-center">
        <h2 className="heading-subsection flex items-center">
          <span className="h-2 w-2 rounded-full bg-purple-500 mr-2 shrink-0"></span>
          {name}
        </h2>
        <div className="flex space-x-1 bg-gray-800 rounded-lg p-1 self-start sm:self-auto">
          <button
            onClick={() => setView('clinician')}
            className={`px-3 py-1.5 rounded text-xs transition-colors cursor-pointer ${
              view === 'clinician' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            For Clinicians
          </button>
          <button
            onClick={() => setView('patient')}
            className={`px-3 py-1.5 rounded text-xs transition-colors cursor-pointer ${
              view === 'patient' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            For Patients
          </button>
        </div>
      </div>

      <div className="px-4 sm:px-5 py-3 border-b border-gray-800 flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-wide text-gray-500 mr-1">Reply language</span>
        {LANGUAGES.map((l) => (
          <button
            key={l.code}
            onClick={() => setLanguage(l.code)}
            title={l.validated
              ? 'Clinically validated'
              : 'Translation not yet clinician-validated — review before relying on it'}
            className={`px-2.5 py-1 rounded text-xs transition-colors cursor-pointer border ${
              language === l.code
                ? 'bg-purple-600 text-white border-purple-500'
                : 'text-gray-400 border-gray-700 hover:text-white hover:border-gray-600'
            }`}
          >
            {l.label}
            {!l.validated && <span className="ml-1 text-[9px] align-super opacity-70">beta</span>}
          </button>
        ))}
        {language !== 'en' && (
          <p className="w-full text-[11px] text-amber-400/90 mt-1">
            {LANGUAGES.find((l) => l.code === language)?.label} replies are machine-translated from a
            safety-checked English answer and are <strong>not yet clinician-validated</strong>.
            Confirm anything clinical against the English version.
          </p>
        )}
      </div>

      <div className="p-4 sm:p-5">
        {(severity || risk) && (
          <div className="flex items-center gap-4 mb-4 text-sm">
            {severity && (
              <span className="text-gray-400">
                Confidence severity: <span className="text-white font-semibold">{severity}</span>
              </span>
            )}
            {risk && (
              <span className="text-gray-400">
                Risk tier: <span className={`font-semibold ${riskColor}`}>{risk}</span>
              </span>
            )}
          </div>
        )}

        <div className="text-gray-200 text-sm sm:text-base">
          <ZafyaMarkdown>{view === 'clinician' ? assistant.clinician : assistant.patient}</ZafyaMarkdown>
        </div>

        {disclaimer && (
          <div className="mt-5 bg-gray-800/60 border border-gray-700 rounded-lg p-3">
            <p className="text-xs text-gray-400 italic">{disclaimer}</p>
          </div>
        )}

        {/* Follow-up chat with Zafya AI */}
        <div className="mt-6 border-t border-gray-800 pt-5">
          <p className="text-sm font-medium text-gray-300 mb-3">
            Ask {name} a follow-up question {view === 'clinician' ? '(clinician mode)' : '(patient mode)'}
          </p>

          {messages.length > 0 && (
            <div ref={scrollRef} className="max-h-80 sm:max-h-96 overflow-y-auto space-y-3 mb-3 pr-1">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`text-sm rounded-lg px-3 py-2 break-words ${
                    m.role === 'user'
                      ? 'bg-purple-900/30 text-purple-100 ml-4 sm:ml-8'
                      : 'bg-gray-800 text-gray-200 mr-4 sm:mr-8'
                  }`}
                >
                  {m.role === 'assistant' ? <ZafyaMarkdown>{m.content}</ZafyaMarkdown> : m.content}
                  {m.role === 'assistant' && (m.languageDowngraded || m.offline || (m.language && m.language !== 'en')) && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {m.languageDowngraded && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-300 border border-amber-700/50">
                          Shown in English — {LANGUAGES.find((l) => l.code === m.languageRequested)?.label || m.languageRequested} unavailable
                        </span>
                      )}
                      {!m.languageDowngraded && m.language !== 'en' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-900/40 text-purple-300 border border-purple-700/50">
                          {LANGUAGES.find((l) => l.code === m.language)?.label} · not clinician-validated
                        </span>
                      )}
                      {m.offline && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-300 border border-gray-700">
                          Offline model
                        </span>
                      )}
                    </div>
                  )}
                  {m.role === 'assistant' && analysisId && m.turnId && (
                    <div className="mt-2 flex items-center gap-1 -ml-2">
                      <button
                        onClick={() => handleFeedback(m.turnId, 'up')}
                        disabled={!!m.feedbackSent}
                        className={`h-8 w-8 flex items-center justify-center rounded cursor-pointer disabled:cursor-default ${m.feedbackSent === 'up' ? 'text-green-400' : 'text-gray-500 hover:text-gray-300'}`}
                        title="Helpful"
                        aria-label="Mark this response as helpful"
                      >
                        👍
                      </button>
                      <button
                        onClick={() => handleFeedback(m.turnId, 'down')}
                        disabled={!!m.feedbackSent}
                        className={`h-8 w-8 flex items-center justify-center rounded cursor-pointer disabled:cursor-default ${m.feedbackSent === 'down' ? 'text-red-400' : 'text-gray-500 hover:text-gray-300'}`}
                        title="Not helpful"
                        aria-label="Mark this response as not helpful"
                      >
                        👎
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {sending && (
                <div className="text-sm rounded-lg px-3 py-2 bg-gray-800 text-gray-500 mr-4 sm:mr-8 italic">
                  {name} is typing…
                </div>
              )}
            </div>
          )}

          {chatError && <p className="text-xs text-red-400 mb-2">{chatError}</p>}

          <div className="flex gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                view === 'clinician'
                  ? 'e.g. What differentials should I consider given this finding?'
                  : 'e.g. What does this result mean for me?'
              }
              rows={1}
              className="flex-1 resize-none bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500"
            />
            <button
              onClick={handleSend}
              disabled={sending || !input.trim()}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors shrink-0 ${
                sending || !input.trim()
                  ? 'bg-purple-900/40 text-purple-300/50 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-700 text-white cursor-pointer'
              }`}
            >
              Send
            </button>
          </div>
          <p className="text-[11px] text-gray-500 mt-2">
            {name} can answer general medical questions localized for African healthcare contexts.
            It does not replace clinical judgement or a prescription.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AssistantPanel;
